export interface RoutingStepRow {
  id?: string;
  stepId?: string;
  name?: string;
  stepOrder?: number;
  sortOrder?: number;
  status?: string;
  plannedSetupTime?: number;
  plannedSpeed?: number;
  plannedSetupWaste?: number;
  plannedRunWastePct?: number;
  estDurationHours?: number;
  expectedOutput?: number;
  standardQuantity?: number;
  isOutsourced?: boolean;
  productionUnit?: 'SHEET' | 'PCS';
  targetProductId?: string;
  targetProductName?: string;
  targetDetailId?: string;
}

export const TECHNOLOGY_SEQUENCE_MAP: Record<string, { rank: number; unit: 'SHEET' | 'PIECE'; group: string }> = {
  // 1. Cắt mộc & Xả giấy
  'PROC-XEN': { rank: 10, unit: 'SHEET', group: 'CUTTING' },
  'PROC-CAT': { rank: 10, unit: 'SHEET', group: 'CUTTING' },
  'PROC-CAT-MOC': { rank: 10, unit: 'SHEET', group: 'CUTTING' },
  'PROC-XA-GIAY': { rank: 10, unit: 'SHEET', group: 'CUTTING' },

  // 2. In ấn
  'PROC-IN': { rank: 20, unit: 'SHEET', group: 'PRINTING' },
  'PROC-IN_4_M_U_CMYK': { rank: 20, unit: 'SHEET', group: 'PRINTING' },
  'PROC-IN-OFFSET': { rank: 20, unit: 'SHEET', group: 'PRINTING' },
  'PROC-IN-KTS': { rank: 20, unit: 'SHEET', group: 'PRINTING' },
  'PROC-IN-LUA': { rank: 25, unit: 'SHEET', group: 'PRINTING' },

  // 3. Cán màng & Tráng phủ
  'PROC-CAN': { rank: 30, unit: 'SHEET', group: 'COATING' },
  'PROC-CAN-BONG': { rank: 30, unit: 'SHEET', group: 'COATING' },
  'PROC-CAN-MO': { rank: 30, unit: 'SHEET', group: 'COATING' },
  'PROC-PHU-UV': { rank: 35, unit: 'SHEET', group: 'COATING' },
  'PROC-PHU-BONG': { rank: 35, unit: 'SHEET', group: 'COATING' },

  // 4. Gia công hiệu ứng
  'PROC-EP-KIM': { rank: 40, unit: 'SHEET', group: 'EMBELLISHMENT' },
  'PROC-EP-NHU': { rank: 40, unit: 'SHEET', group: 'EMBELLISHMENT' },
  'PROC-BE-NOI': { rank: 45, unit: 'SHEET', group: 'EMBELLISHMENT' },
  'PROC-BE-CHIM': { rank: 45, unit: 'SHEET', group: 'EMBELLISHMENT' },
  'PROC-UV-DINH-HINH': { rank: 48, unit: 'SHEET', group: 'EMBELLISHMENT' },
  'PROC-AUTO-6QFXV': { rank: 48, unit: 'SHEET', group: 'EMBELLISHMENT' },

  // 5. Bế đứt
  'PROC-BE': { rank: 50, unit: 'SHEET', group: 'DIE_CUTTING' },
  'PROC-BE-DUT': { rank: 50, unit: 'SHEET', group: 'DIE_CUTTING' },
  'PROC-BOI': { rank: 52, unit: 'SHEET', group: 'DIE_CUTTING' },
  // Cắt thành phẩm: sau in / cán / ép (cùng thứ tự với máy chủ, technology-sequence.ts)
  'PROC-CAT-TP': { rank: 55, unit: 'SHEET', group: 'DIE_CUTTING' },

  // 6. Sau bế / Bóc lề
  'PROC-TACH-PHOI': { rank: 60, unit: 'PIECE', group: 'STRIPPING' },
  'PROC-BOC-LE': { rank: 60, unit: 'PIECE', group: 'STRIPPING' },

  // 7. Dán hộp
  'PROC-DAN': { rank: 70, unit: 'PIECE', group: 'GLUING' },
  'PROC-DAN-DAY': { rank: 70, unit: 'PIECE', group: 'GLUING' },
  'PROC-DAN-CANH': { rank: 70, unit: 'PIECE', group: 'GLUING' },
  'PROC-DAN-CUA-SO': { rank: 72, unit: 'PIECE', group: 'GLUING' },
  'PROC-GAP-DAN': { rank: 70, unit: 'PIECE', group: 'GLUING' },

  // 8. Đóng gói & QC
  'PROC-DONG-GOI': { rank: 80, unit: 'PIECE', group: 'PACKING_QC' },
  'PROC-KCS': { rank: 85, unit: 'PIECE', group: 'PACKING_QC' },
  'PROC-FQC': { rank: 85, unit: 'PIECE', group: 'PACKING_QC' },
};

export function resolveProcessTechnologyRank(proc: {
  stepId?: string | null;
  name?: string | null;
  wasteUnit?: string | null;
  priceUnit?: string | null;
}) {
  const stepId = (proc.stepId || '').trim().toUpperCase();
  const name = (proc.name || '').trim().toLowerCase();

  if (stepId && TECHNOLOGY_SEQUENCE_MAP[stepId]) {
    return TECHNOLOGY_SEQUENCE_MAP[stepId];
  }

  if (name.includes('cắt thành phẩm') || stepId.includes('CAT-TP') || stepId.includes('CAT_TP')) {
    return { rank: 55, unit: 'SHEET' as const, group: 'DIE_CUTTING' };
  }
  if (stepId.includes('XEN') || stepId.includes('CAT') || name.includes('xén') || name.includes('cắt mộc') || name.includes('xả giấy')) {
    return { rank: 10, unit: 'SHEET' as const, group: 'CUTTING' };
  }
  if (stepId.includes('IN') || name.includes('in offset') || name.includes('in 4 màu') || name.includes('in kỹ thuật số') || name.includes('in ấn') || name.startsWith('in ')) {
    return { rank: 20, unit: 'SHEET' as const, group: 'PRINTING' };
  }
  if (stepId.includes('CAN') || name.includes('cán màng') || name.includes('cán bóng') || name.includes('cán mờ') || name.includes('tráng phủ')) {
    return { rank: 30, unit: 'SHEET' as const, group: 'COATING' };
  }
  if (stepId.includes('EP') || stepId.includes('NHU') || stepId.includes('FOIL') || name.includes('ép kim') || name.includes('ép nhũ')) {
    return { rank: 40, unit: 'SHEET' as const, group: 'EMBELLISHMENT' };
  }
  if (name.includes('bế nổi') || name.includes('bế chìm')) {
    return { rank: 45, unit: 'SHEET' as const, group: 'EMBELLISHMENT' };
  }
  if (stepId.includes('UV') || name.includes('uv định hình') || name.includes('spot uv') || stepId === 'PROC-AUTO-6QFXV') {
    return { rank: 48, unit: 'SHEET' as const, group: 'EMBELLISHMENT' };
  }
  if (stepId.includes('BE') || name.includes('bế đứt') || name.startsWith('bế ')) {
    return { rank: 50, unit: 'SHEET' as const, group: 'DIE_CUTTING' };
  }
  if (stepId.includes('TACH') || stepId.includes('BOC') || name.includes('tách phôi') || name.includes('bóc lề')) {
    return { rank: 60, unit: 'PIECE' as const, group: 'STRIPPING' };
  }
  if (stepId.includes('DAN') || name.includes('dán') || name.includes('gấp dán') || name.includes('dán hộp')) {
    return { rank: 70, unit: 'PIECE' as const, group: 'GLUING' };
  }
  if (stepId.includes('DONG_GOI') || stepId.includes('PACK') || name.includes('đóng gói') || name.includes('đóng thùng') || name.includes('thùng')) {
    return { rank: 80, unit: 'PIECE' as const, group: 'PACKING_QC' };
  }
  if (stepId.includes('QC') || stepId.includes('KCS') || stepId.includes('FQC') || name.includes('kcs') || name.includes('kiểm tra')) {
    return { rank: 85, unit: 'PIECE' as const, group: 'PACKING_QC' };
  }

  const isPcs = proc.wasteUnit === 'PIECE' || proc.priceUnit === 'PER_UNIT';
  return {
    rank: isPcs ? 75 : 35,
    unit: isPcs ? ('PIECE' as const) : ('SHEET' as const),
    group: isPcs ? 'GLUING' : 'COATING',
  };
}

/**
 * Hợp nhất danh sách quy trình công đoạn chuẩn hóa ngành in (ISO / Offset Gang-run):
 * 1. Sheet-Level Dedup: Công đoạn tờ lớn (Xả, In, Cán, Bế) chỉ xuất hiện DUY NHẤT 1 lần cho cả bài trên S_run.
 * 2. Piece-Level Split: Công đoạn sau bế (Dán hộp, Đóng gói) phân rã theo từng mã sản phẩm con [Mã SP] (Q_i).
 * 3. Technology Sequence: Neo cứng thứ tự theo ma trận công nghệ in ấn 1..N.
 */
export function mergeUniqueRoutingSteps(
  primaryProductProcesses: any[] = [],
  additionalProductsProcesses: any[][] | Array<{ processes?: any[]; productCode?: string; productName?: string; quantity?: number; orderDetailId?: string }> = [],
  options?: {
    runSheetsNeeded?: number;
    primaryProductName?: string;
    primaryProductCode?: string;
    primaryQuantity?: number;
    isMultiProduct?: boolean;
  }
): RoutingStepRow[] {
  const steps: RoutingStepRow[] = [];
  const seenSheetKeys = new Set<string>();

  const isMulti = options?.isMultiProduct || (Array.isArray(additionalProductsProcesses) && additionalProductsProcesses.length > 0);

  // Helper process mapper
  const mapProcessToRow = (
    p: any,
    targetInfo?: { productCode?: string; productName?: string; quantity?: number; orderDetailId?: string }
  ): RoutingStepRow => {
    const sId = p.process?.stepId || p.stepId || p.processId || '';
    const sName = p.process?.name || p.name || '';
    const tech = resolveProcessTechnologyRank({ stepId: sId, name: sName, ...p.process });
    const isPcs = tech.unit === 'PIECE';

    let displayName = sName;
    if (isPcs && isMulti && (targetInfo?.productCode || targetInfo?.productName)) {
      const codeOrName = targetInfo.productCode || targetInfo.productName;
      if (!displayName.includes(`[${codeOrName}]`)) {
        displayName = `${sName} - [${codeOrName}]`;
      }
    }

    const stdQty = isPcs
      ? (targetInfo?.quantity ?? p.expectedOutput ?? p.standardQuantity ?? 0)
      : (options?.runSheetsNeeded ?? p.expectedOutput ?? p.standardQuantity ?? 0);

    return {
      id: p.id || `step-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      stepId: sId,
      name: displayName,
      productionUnit: isPcs ? 'PCS' : 'SHEET',
      estDurationHours: p.process?.productionTime
        ? Number((p.process.productionTime / 60).toFixed(2))
        : (p.estDurationHours || 0),
      plannedSetupTime: p.setupTimeMinutes ?? p.plannedSetupTime ?? 0,
      plannedSpeed: p.speedPerHour ?? p.plannedSpeed ?? 0,
      plannedSetupWaste: p.setupWasteQty ?? p.plannedSetupWaste ?? 0,
      plannedRunWastePct: p.runWastePercent ?? p.plannedRunWastePct ?? 0,
      expectedOutput: stdQty,
      standardQuantity: stdQty,
      status: p.status || 'PLANNED',
      isOutsourced: Boolean(p.isOutsourced || p.process?.isOutsourced),
      targetProductId: targetInfo?.productCode || targetInfo?.productName,
      targetProductName: targetInfo?.productName,
      targetDetailId: targetInfo?.orderDetailId,
      sortOrder: p.sortOrder ?? p.stepOrder ?? 0,
    };
  };

  // 1. Process Primary Product
  const primaryInfo = {
    productCode: options?.primaryProductCode,
    productName: options?.primaryProductName,
    quantity: options?.primaryQuantity,
  };

  for (const p of primaryProductProcesses || []) {
    if (p.isDeleted) continue;
    const row = mapProcessToRow(p, primaryInfo);
    const tech = resolveProcessTechnologyRank({ stepId: row.stepId, name: row.name });

    if (tech.unit === 'SHEET') {
      const groupKey = ['PRINTING', 'CUTTING', 'DIE_CUTTING'].includes(tech.group) ? tech.group : (row.stepId || row.name || 'unknown');
      if (!seenSheetKeys.has(groupKey)) {
        seenSheetKeys.add(groupKey);
        steps.push(row);
      }
    } else {
      steps.push(row);
    }
  }

  // 2. Process Additional Products
  for (const item of additionalProductsProcesses) {
    let procs: any[] = [];
    let targetInfo: { productCode?: string; productName?: string; quantity?: number; orderDetailId?: string } = {};

    if (Array.isArray(item)) {
      procs = item;
    } else if (item && typeof item === 'object') {
      procs = item.processes || [];
      targetInfo = {
        productCode: item.productCode,
        productName: item.productName,
        quantity: item.quantity,
        orderDetailId: item.orderDetailId,
      };
    }

    for (const p of procs) {
      if (p.isDeleted) continue;
      const row = mapProcessToRow(p, targetInfo);
      const tech = resolveProcessTechnologyRank({ stepId: row.stepId, name: row.name });

      if (tech.unit === 'SHEET') {
        const groupKey = ['PRINTING', 'CUTTING', 'DIE_CUTTING'].includes(tech.group) ? tech.group : (row.stepId || row.name || 'unknown');
        if (!seenSheetKeys.has(groupKey)) {
          seenSheetKeys.add(groupKey);
          steps.push(row);
        }
      } else {
        steps.push(row);
      }
    }
  }

  // 3. Sort by Technology Sequence Rank (Invariant 4)
  steps.sort((a, b) => {
    const rankA = resolveProcessTechnologyRank({ stepId: a.stepId, name: a.name }).rank;
    const rankB = resolveProcessTechnologyRank({ stepId: b.stepId, name: b.name }).rank;
    if (rankA !== rankB) return rankA - rankB;
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });

  // 4. Renumber stepOrder strictly 1..N
  return steps.map((step, idx) => ({
    ...step,
    stepOrder: idx + 1,
  }));
}
